import {
  Cell,
  HeadCell,
  HeadRow,
  Row,
  SortingHeadCell,
  Table,
} from '@dapp/multi-lib/sdaoui/Table';
import { Typography } from '@dapp/multi-lib/sdaoui/Typography';
import { AssetIcon } from '@dapp/multi-lib/sdaoui/AssetIcon';
import { useTranslation } from 'react-i18next';
import { Body, Body1, Body2 } from 'components/Typography';
import { VaultPrice } from 'structure/pv2/dynavault/components/VaultPrice';
import { UsdValue } from 'components/UsdValue';
import { VaultPosition } from 'structure/pv2/dynavault/components/VaultPosition';
import Link from 'next/link';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { DynaVault } from '../../../../api/DynaVaultsAPI';
import { getNetworkIconUrlByChaiId, getStaticAssetUrl } from 'utils/url';
import { getNetworkByChainId } from '@dapp/multi-lib/network';
import useIsMobile from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { useConnection } from 'wagmi';
import {
  getRiskLevelColorClass,
  getRiskLevelLabel,
} from 'structure/pv2/vault/utils/vault';
import { cx } from 'class-variance-authority';
import { useVaultOnChainData } from 'structure/pv2/dynavault/hooks/useVaultListBlockchain';
import { useVaultListContext } from 'structure/pv2/dynavault/context/VaultListContext';
import { VaultAPY } from 'structure/pv2/vault/components/VaultAPY';
import { VaultStatus } from 'structure/pv2/dynavault/components/VaultStatus';

type DynaVaultListTableProps = {
  vaultList: DynaVault[];
};

export const VaultListTable = (props: DynaVaultListTableProps) => {
  const { vaultList } = props;

  const { t } = useTranslation('vault');

  const isMobile = useIsMobile();

  const { changeSortDirection, getSortDirection } = useVaultListContext();

  if (isMobile) {
    return (
      <div className='flex flex-col gap-4'>
        {vaultList.map((vault) => (
          <TableRowMobile key={vault.id} vault={vault} />
        ))}
      </div>
    );
  }

  return (
    <Table
      role='grid'
      $gridTemplateColumns='repeat(7, minmax(max-content, 1fr))'
    >
      <HeadRow>
        <HeadCell $sticky={false}>{t('VAULT_TABLE.HEAD_VAULT')}</HeadCell>
        <SortingHeadCell
          sortDirection={getSortDirection('price')}
          onSort={() => {
            changeSortDirection('price');
          }}
        >
          {t('VAULT_TABLE.HEAD_PRICE')}
        </SortingHeadCell>
        <SortingHeadCell
          sortDirection={getSortDirection('tvl')}
          onSort={() => {
            changeSortDirection('tvl');
          }}
        >
          {t('VAULT_TABLE.HEAD_TVL')}
        </SortingHeadCell>
        <SortingHeadCell
          sortDirection={getSortDirection('position')}
          onSort={() => {
            changeSortDirection('position');
          }}
        >
          {t('VAULT_TABLE.HEAD_POSITION')}
        </SortingHeadCell>
        <SortingHeadCell
          sortDirection={getSortDirection('apy')}
          onSort={() => {
            changeSortDirection('apy');
          }}
        >
          {t('VAULT_TABLE.APY')}
        </SortingHeadCell>
        <HeadCell $sticky={false}>{t('VAULT_TABLE.STATUS')}</HeadCell>
        <HeadCell $sticky={false}>&nbsp;</HeadCell>
      </HeadRow>
      {vaultList.map((vault) => (
        <TableRow key={vault.id} vault={vault} />
      ))}
    </Table>
  );
};

type TableRowProps = {
  vault: DynaVault;
};

const TableRow = (props: TableRowProps) => {
  const { vault } = props;
  const { t } = useTranslation('vault');
  const connection = useConnection();

  const vaultOnChainData = useVaultOnChainData({
    vaultAddress: vault.address,
    chainId: vault.chainId,
    walletAddress: connection.address as string,
  });

  // Convert vault price (in deposit token) to USD using oracle
  const vaultPriceInUsd = vaultOnChainData.data
    ? Number(vaultOnChainData.data.vaultPrice)
    : 0;

  const tvl = vaultOnChainData.data ? vaultOnChainData.data.tvlUsd : '0';

  return (
    <Row>
      <Cell className='!pr-[56px]'>
        <div className='flex w-[180px] flex-row gap-3'>
          <AssetIcon
            assetImg={getStaticAssetUrl(
              '/sfi/static/image/icons/dynasets/dynDYDX.png'
            )}
            networkImg={getNetworkIconUrlByChaiId(vault.chainId)}
            assetName={vault.symbol}
            networkName={getNetworkByChainId(vault.chainId)?.name}
          />
          <div className='flex flex-col'>
            <Body2 className='lowercase text-[#D9D9D9] first-letter:uppercase'>
              {vault.type}
            </Body2>
            <Body1>{vault.symbol}</Body1>
          </div>
        </div>
      </Cell>
      <Cell>
        <Typography className='text-sm'>
          <VaultPrice
            price={vaultPriceInUsd}
            className='items-start'
            showPerformance={!vault.isClosed}
            performance={`${vault.twentyFourHourPerformance}%`}
          />
        </Typography>
      </Cell>
      <Cell>
        <Typography className='text-sm'>
          <UsdValue size='small' value={tvl} />
        </Typography>
      </Cell>
      <Cell>
        <VaultPosition className='items-start' vault={vault} />
      </Cell>
      <Cell>
        <VaultAPY vault={vault} className='font-normal' />
      </Cell>
      <Cell>
        <VaultStatus vault={vault} />
      </Cell>
      <Cell>
        <Button
          component={Link}
          size='small'
          href={`/vaults/${vault.address}:${vault.chainId}`}
        >
          {t('EXPLORE')}
        </Button>
      </Cell>
    </Row>
  );
};

const TableRowMobile = (props: TableRowProps) => {
  const { vault } = props;
  const { t } = useTranslation('common');

  const account = useConnection();

  const vaultOnChainData = useVaultOnChainData({
    vaultAddress: vault.address,
    chainId: vault.chainId,
    walletAddress: account.address as string,
  });

  // Convert vault price (in deposit token) to USD using oracle
  const vaultPriceInUsd = vaultOnChainData.data
    ? Number(vaultOnChainData.data.vaultPrice)
    : 0;

  const tvl = vaultOnChainData.data ? vaultOnChainData.data.tvlUsd : '0';

  return (
    <div
      className={cx(
        'rounded-main items-center gap-2 border border-[#48484e] bg-[#121315] px-4 py-2',
        'grid grid-cols-[min-content_minmax(0,1fr)_minmax(0,1fr)]',
        'md:grid-cols-[min-content_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_min-content]'
      )}
    >
      <AssetIcon
        size='small'
        assetImg={'https://singularitydao.ai/image/icons/dynasets/dynDYDX.png'}
        networkImg={getNetworkIconUrlByChaiId(vault.chainId)}
        assetName={vault.symbol}
        networkName={getNetworkByChainId(vault.chainId)?.name}
      />
      <div className=' flex flex-row items-center gap-3'>
        <div className='flex flex-col gap-1'>
          <Body className='text-sm lowercase text-[#D9D9D9] first-letter:uppercase'>
            {vault.type}
          </Body>
          <Body className='text-base'>{vault.symbol}</Body>
          <VaultStatus vault={vault} className='text-xs' />
        </div>
      </div>
      <div className='flex flex-col gap-1'>
        <div className='flex flex-row items-center gap-1'>
          <Body className='text-sm text-white-500'>Price:</Body>
          <UsdValue
            size='xsmall'
            value={vaultPriceInUsd}
            valueClassName='text-sm'
            symbolClassName='text-xs'
          />
        </div>
        <div className='flex flex-row items-center gap-1'>
          <Body className='text-sm text-white-500'>TVL:</Body>
          <UsdValue
            size='xsmall'
            value={tvl}
            valueClassName='text-sm'
            symbolClassName='text-xs'
          />
        </div>
      </div>

      <div className='md:hidden' />

      <div className='flex flex-col gap-1'>
        <div className='flex flex-row items-center gap-1'>
          <Body className='text-sm text-white-500'>APY:</Body>
          <VaultAPY vault={vault} className='text-sm font-normal' />
        </div>

        <div className='flex flex-row items-center gap-1'>
          <Body className='text-nowrap text-sm text-white-500'>
            {t('DYNAVAULT.RISK_LEVEL')}
          </Body>
          <Body
            className={cx('text-sm', getRiskLevelColorClass(vault.riskLevel))}
          >
            {getRiskLevelLabel(vault.riskLevel)}
          </Body>
        </div>
      </div>
      <div className='flex flex-col gap-1'>
        <Body className='text-sm text-white-500'>Position:</Body>
        <VaultPosition
          className='flex-row items-center gap-1 !text-sm'
          vault={vault}
        />
      </div>

      <div className='-col-end-1 flex flex-row justify-end'>
        <Button
          component={Link}
          size='small'
          href={`/vaults/${vault.address}:${vault.chainId}`}
        >
          {t('EXPLORE')}
        </Button>
      </div>
    </div>
  );
};
